import { formatPrice } from '@/common/constant';
import type { WineProductSearchResult } from '@/common/domain-model/product-search';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';
import { BulbOutlined } from '@ant-design/icons';
import { Card, Pagination, Select, Tag, Tooltip } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

type SortOption = 'similarity' | 'rating' | 'price';

interface ProductSearchResultsProps {
  products: WineProductSearchResult[];
}

function WineProductCard({
  product,
}: {
  product: WineProductSearchResult;
}) {
  const { similarity } = product;
  const { currency } = useCurrencySymbol();
  const rating = product.data.rating;
  const matchPercentage = `${(similarity * 100).toFixed(2)}%`;
  const ratingColor = useMemo(() => {
    if (rating && rating >= 90) return 'text-green-600';
    if (rating && rating >= 80) return 'text-blue-600';
    if (rating && rating >= 70) return 'text-yellow-600';
    return 'text-gray-600';
  }, [rating]);

  const imageUrl = product.data.image_urls?.[0];

  return (
    <Card>
      <div className="flex gap-4">
        {imageUrl && (
          <img
            src={imageUrl}
            alt={product.data.title}
            className="w-24 h-32 object-cover rounded-md bg-muted shrink-0"
            loading="lazy"
          />
        )}
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <h3 className="text-lg font-semibold break-words pr-4">
                {product.data.title}
              </h3>
            </div>
            <div className="flex flex-col items-end text-sm shrink-0">
              {rating && (
                <div className="flex items-center gap-1">
                  <span className={`font-medium ${ratingColor}`}>
                    {rating.toFixed(1)}
                  </span>
                </div>
              )}
              <div className="text-xs text-muted-foreground">
                Match: {matchPercentage}
              </div>
            </div>
          </div>
          <p className="text-sm text-muted-foreground break-words">
            {product.data.description || 'No description available'}
          </p>
          <div className="flex flex-wrap gap-2 text-sm">
            {[
              {
                id: 'wine_type',
                label: product.data.wine_type,
                value: product.data.wine_type,
              },
              {
                id: 'grape',
                label: product.data.grape,
                value: product.data.grape,
              },
              {
                id: 'producer',
                label: product.data.producer,
                value: product.data.producer,
              },
              {
                id: 'country',
                label: product.data.country,
                value: product.data.country,
              },
              {
                id: 'year',
                label: product.data.year,
                value: product.data.year,
              },
              {
                id: 'alcohol',
                label: product.data.alcohol,
                value: product.data.alcohol
                  ? `${product.data.alcohol}% ABV`
                  : undefined,
              },
              {
                id: 'source',
                label: product.source_name,
                value: product.source_name,
              },
              {
                id: 'supplier',
                label: product.supplier_name,
                value: product.supplier_name,
              },
            ].map((attr) =>
              attr.label && attr.value ? (
                <Tag
                  color="#18181b1a"
                  className="!text-black"
                  key={`${attr.id}-${attr.value}`}
                  bordered={false}
                >
                  {attr.value}
                </Tag>
              ) : null,
            )}
          </div>
          <div className="flex items-center justify-between gap-4">
            <span className="font-semibold shrink-0">
              {product.data.price
                ? formatPrice(product.data.price, currency)
                : 'Price not available'}
            </span>
          </div>
        </div>
      </div>
    </Card>
  );
}

function KeyboardShortcutsInfo() {
  return (
    <Tooltip
      title={
        <div className="text-sm">
          <p className="font-medium mb-1">Keyboard Shortcuts</p>
          <div className="space-y-1">
            <p>← Previous page</p>
            <p>→ Next page</p>
          </div>
        </div>
      }
    >
      <button
        type="button"
        className="p-1 hover:bg-muted rounded-md"
        aria-label="Keyboard shortcuts"
      >
        <BulbOutlined className="h-4 w-4 text-muted-foreground" />
      </button>
    </Tooltip>
  );
}

export function ProductSearchResults({ products }: ProductSearchResultsProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<SortOption>('similarity');
  const [isSorting, setIsSorting] = useState(false);
  const itemsPerPage = 4;
  const searchResultsRef = useRef<HTMLDivElement>(null);

  // Reset page when products change
  useEffect(() => {
    setCurrentPage(1);
  }, []);

  // Add keyboard shortcuts to control pagination
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        return;
      }

      switch (e.key) {
        case 'ArrowLeft':
          if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
            handleScroll();
          }
          break;
        case 'ArrowRight':
          if (currentPage < Math.ceil(products.length / itemsPerPage)) {
            setCurrentPage(currentPage + 1);
            handleScroll();
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentPage, products.length]);

  const handleScroll = useCallback(() => {
    setTimeout(() => {
      if (searchResultsRef.current) {
        searchResultsRef.current.style.scrollMarginTop = '30px';
        searchResultsRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }, 100);
  }, []);

  // Sort products based on selected sorting option
  const sortedProducts = useMemo(() => {
    const sorted = [...products];
    switch (sortBy) {
      case 'similarity':
        sorted.sort((a, b) => b.similarity - a.similarity);
        break;
      case 'rating':
        sorted.sort((a, b) => {
          const ratingA = a.data.rating ?? 0;
          const ratingB = b.data.rating ?? 0;
          return ratingB - ratingA;
        });
        break;
      case 'price':
        sorted.sort((a, b) => {
          const priceA = a.data.price ?? 0;
          const priceB = b.data.price ?? 0;
          return priceB - priceA;
        });
        break;
    }
    return sorted;
  }, [products, sortBy]);

  // Calculate pagination information
  const { totalPages, currentProducts } = useMemo(() => {
    const total = Math.ceil(sortedProducts.length / itemsPerPage);
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return {
      totalPages: total,
      currentProducts: sortedProducts.slice(start, end),
    };
  }, [sortedProducts, currentPage]);

  const handlePageChange = useCallback(
    (page: number) => {
      setCurrentPage(page);
      handleScroll();
    },
    [handleScroll],
  );

  const handleSortChange = useCallback(
    (value: string) => {
      setIsSorting(true);
      setSortBy(value as SortOption);
      setCurrentPage(1);
      handleScroll();
      // Reset sorting state for animation effect
      setTimeout(() => setIsSorting(false), 300);
    },
    [handleScroll],
  );

  return (
    <div ref={searchResultsRef} className={'space-y-4 w-full pb-8'}>
      <div className="text-lg font-semibold">Search Results</div>

      {products.length > 0 ? (
        <>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-2 min-w-[200px] flex-1">
              <p className="text-sm text-muted-foreground whitespace-nowrap">
                Found {products.length} matching wines
              </p>
              <KeyboardShortcutsInfo />
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-sm text-muted-foreground whitespace-nowrap">
                Sort by:
              </span>
              <Select
                value={sortBy}
                onChange={handleSortChange}
                style={{ width: 180 }}
                options={[
                  { value: 'similarity', label: 'Best Match' },
                  { value: 'rating', label: 'Highest Rating' },
                  { value: 'price', label: 'Price (High to Low)' },
                ]}
              />
            </div>
          </div>
          <div className="min-w-0">
            <div className="grid gap-4">
              {currentProducts.map((product) => (
                <div
                  key={product.id}
                  className="transform transition-all duration-300 ease-in-out"
                  style={{
                    opacity: isSorting ? 0.5 : 1,
                    transform: isSorting ? 'translateY(10px)' : 'translateY(0)',
                  }}
                >
                  <WineProductCard product={product} />
                </div>
              ))}
            </div>
          </div>

          {totalPages > 1 && (
            <div className="mt-4 pt-4 border-t">
              <Pagination
                current={currentPage}
                total={products.length}
                pageSize={itemsPerPage}
                onChange={handlePageChange}
                showSizeChanger={false}
                className="flex justify-center"
              />
            </div>
          )}
        </>
      ) : (
        <div className="text-center text-gray-500">No products found</div>
      )}
    </div>
  );
}
